import { useRef, useState } from 'react';
import { uploadToCloudinary, uploadErrorMessage } from '@/lib/cloudinaryUpload';
import { Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const MAX_MB = 100;
const VALID_TYPES = ['video/mp4', 'video/webm', 'video/quicktime', 'video/ogg'];

export const VideoUploader = ({ onUploaded, className = '' }) => {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!VALID_TYPES.includes(file.type)) {
      toast.error('Please choose an MP4, WebM, MOV or OGG video.');
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`Video is too large. Maximum size is ${MAX_MB}MB.`);
      return;
    }
    setProgress(0);
    try {
      const url = await uploadToCloudinary(file, { resourceType: 'video', onProgress: setProgress });
      onUploaded(url);
      toast.success('Video uploaded');
    } catch (err) {
      toast.error(uploadErrorMessage(err));
    } finally {
      setProgress(null);
    }
  };

  const uploading = progress !== null;

  return (
    <>
      <input ref={inputRef} type="file" accept="video/*" className="hidden" onChange={handleFile} data-testid="video-file-input" />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className={`inline-flex items-center justify-center gap-2 rounded-md border px-4 py-2 text-sm font-medium transition-colors disabled:opacity-60 ${className}`}
        data-testid="upload-video-btn"
      >
        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        {uploading ? `Uploading ${progress}%` : 'Upload Video'}
      </button>
    </>
  );
};

export default VideoUploader;
